import type { LucideIcon } from "lucide-react";
import { ArrowUp, ArrowUpRight, CalendarClock, ChevronDown, ChevronRight, Download, FileText } from "lucide-react";
import { type ReactNode, type SyntheticEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { MAIN_CONTENT_ID } from "@/lib/main-content";
import { useMediaQuery } from "@/lib/use-media-query";
import { usePageMeta } from "@/lib/use-page-meta";
import { cn } from "@/shared/lib/utils";

import { useHashScroll } from "./use-hash-scroll";
import { useScrollSpy } from "./use-scroll-spy";

/** A table-of-contents entry — must match a SectionHeading `id` on the page. */
export interface DocTocEntry {
	/** The section element id (the scroll-spy + deep-link target). */
	id: string;
	/** The label shown in the TOC rail. */
	label: string;
}

/** A "Related" footer link — an in-app doc route, an external URL, or a download. */
export interface DocRelatedLink {
	id: string;
	label: string;
	/** Short supporting line. */
	description?: string;
	/** Destination: an in-app route ("/docs/..."), an absolute URL, or a download. */
	href: string;
	/** Mark external (opens in a new tab with an out-arrow). */
	external?: boolean;
	/** Render as a download affordance (download icon, native download). */
	download?: boolean;
	/** Optional icon override (defaults to a document / download / out icon). */
	icon?: LucideIcon;
}

/**
 * A linked ancestor crumb. It must name its own analytics id, so the same
 * ancestor keeps one id in every state of a template (loaded, gated,
 * unavailable, not found).
 */
export interface BreadcrumbLink {
	label: string;
	/** In-app route of the ancestor page. */
	to: string;
	/** Stable `data-mp-id` for the link. */
	mpId: string;
}

/** The current page: always last, never linked. */
export interface BreadcrumbCurrent {
	label: string;
	to?: never;
	mpId?: never;
}

/**
 * A full trail: linked ancestors, then exactly one unlinked current page. The
 * tuple puts the "last item is the current page" rule in the type, so a link on
 * the final crumb (which the renderer would drop) fails to compile.
 */
export type BreadcrumbTrail = readonly [...BreadcrumbLink[], BreadcrumbCurrent];

interface DocLayoutProps {
	/** Purple eyebrow above the title (e.g. "Whitepaper", "Architecture"). */
	eyebrow: string;
	/** Page title (the H1 + the document <title>), set in the Geist display face. */
	title: string;
	/** One-paragraph lede under the title. */
	lede: string;
	/** Category label shown in the meta row (e.g. "Security"). */
	category: string;
	/** Last-updated label shown in the meta row (omit/empty to hide). */
	lastUpdated?: string;
	/** Ordered TOC entries (each id must match a SectionHeading on the page). */
	toc: ReadonlyArray<DocTocEntry>;
	/** "Related" footer links. */
	related?: ReadonlyArray<DocRelatedLink>;
	/** The page body — a stack of <section>s with SectionHeadings. */
	children: ReactNode;
}

const CRUMB_LINK_CLASS =
	"inline-flex min-h-6 items-center whitespace-nowrap py-1 font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * The one breadcrumb for every doc template: write-ups, guides, and their gated,
 * unavailable and not-found states. Location-based (where the page sits in the
 * site), the current page last and unlinked, kept to one line on a phone by
 * truncating the current title (NN/g: https://www.nngroup.com/articles/breadcrumbs/).
 * Links carry `py-1` so the target clears the 24 px floor (WCAG 2.2 SC 2.5.8) and a
 * focus ring with no `rounded-*` class, so the global card lift never catches them.
 */
export function Breadcrumb({ items, className }: { items: BreadcrumbTrail; className?: string }) {
	return (
		<nav aria-label="Breadcrumb" className={cn("print:hidden", className)}>
			<ol className="flex items-center gap-1.5 text-sm">
				{items.map((item, index) => {
					const last = index === items.length - 1;
					return (
						<li
							key={`${item.label}-${index}`}
							className={cn("flex items-center gap-1.5", last ? "min-w-0" : "shrink-0")}
						>
							{item.to && !last ? (
								<Link to={item.to} data-mp-id={item.mpId} className={CRUMB_LINK_CLASS}>
									{item.label}
								</Link>
							) : (
								<span aria-current={last ? "page" : undefined} className="truncate py-1 font-medium text-foreground">
									{item.label}
								</span>
							)}
							{!last && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" />}
						</li>
					);
				})}
			</ol>
		</nav>
	);
}

/** The `lg` breakpoint (tailwind default), where the TOC becomes the sticky rail. */
const RAIL_QUERY = "(min-width: 1024px)";

/**
 * The "On this page" table of contents, one landmark at every width.
 *
 * Below `lg` it is a native `<details>` at the top of the article: closed by
 * default, labelled with the section count, and closed again after a pick, so a
 * phone reader reaches any section in two taps without a second nav (NN/g: keep
 * in-page links at the top of the content on mobile, and a sticky TOC on larger
 * screens that marks the current section: https://www.nngroup.com/articles/in-page-links/).
 * At `lg+` a media-query hook forces it open and hides the summary, so the rail
 * reads as today. Native `<details>` over the Radix Accordion: no JS, find-in-page
 * auto-expands it, it prints, and it survives a failed chunk.
 *
 * The STICKY box is the caller's `<aside>` (`lg:sticky lg:top-24 lg:self-start`),
 * never a child of `<details>`: Chrome lays the details content out in a
 * content-height slot, so a sticky child inside it has no room to travel.
 *
 * `open` is mirrored FROM the DOM with `onToggle` (the user can toggle a native
 * details without React), and a pick closes it through the element, never
 * through a stale controlled prop.
 */
export function TableOfContents({ toc }: { toc: ReadonlyArray<DocTocEntry> }) {
	const ids = toc.map((entry) => entry.id);
	const activeId = useScrollSpy(ids);
	// Honor `#section` deep links once the gated/lazy body has mounted — the
	// browser's initial hash scroll fired before this content existed. Lives here
	// so both write-ups (DocLayout) and guides (GuideArticlePage) inherit it.
	useHashScroll();
	const isRail = useMediaQuery(RAIL_QUERY);
	const detailsRef = useRef<HTMLDetailsElement>(null);
	const [open, setOpen] = useState(isRail);

	// The rail is always open; leaving it, the list starts collapsed.
	useEffect(() => {
		setOpen(isRail);
	}, [isRail]);

	// Print the full list even when collapsed, for engines without `::details-content`.
	// The pre-print state is kept in a local, not read from `open`: setting `.open`
	// fires `toggle`, which would otherwise rebind this effect over the new value.
	useEffect(() => {
		if (isRail) {
			return;
		}
		let wasOpen = false;
		const before = () => {
			if (detailsRef.current) {
				wasOpen = detailsRef.current.open;
				detailsRef.current.open = true;
			}
		};
		const after = () => {
			if (detailsRef.current) {
				detailsRef.current.open = wasOpen;
			}
		};
		window.addEventListener("beforeprint", before);
		window.addEventListener("afterprint", after);
		return () => {
			window.removeEventListener("beforeprint", before);
			window.removeEventListener("afterprint", after);
		};
	}, [isRail]);

	if (toc.length === 0) {
		return null;
	}

	const onToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
		setOpen(event.currentTarget.open);
	};

	const closeAfterPick = () => {
		if (!isRail && detailsRef.current) {
			detailsRef.current.open = false;
		}
	};

	return (
		<nav aria-label="On this page">
			<details
				ref={detailsRef}
				open={open}
				onToggle={onToggle}
				data-mp-id="doc-toc"
				className={cn(
					"group/toc rounded-2xl border border-border bg-card lg:rounded-none lg:border-0 lg:bg-transparent",
					"print:rounded-none print:border-0 print:bg-transparent",
				)}
			>
				<summary
					className={cn(
						"flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-4 py-2.5 text-sm font-medium text-foreground",
						"[&::-webkit-details-marker]:hidden",
						"rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
						"lg:hidden print:hidden",
					)}
				>
					<span>
						On this page
						<span className="ml-2 text-xs font-normal text-muted-foreground">
							{toc.length} {toc.length === 1 ? "section" : "sections"}
						</span>
					</span>
					<ChevronDown
						className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open/toc:rotate-180"
						aria-hidden="true"
					/>
				</summary>
				<div className="hidden text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground lg:block">
					On this page
				</div>
				<ul className="space-y-0.5 border-t border-border px-2 pb-2 pt-2 lg:mt-4 lg:border-l lg:border-t-0 lg:border-border/60 lg:p-0">
					{toc.map((entry) => {
						const isActive = entry.id === activeId;
						return (
							<li key={entry.id}>
								<a
									href={`#${entry.id}`}
									onClick={closeAfterPick}
									data-mp-id={`doc-toc-${entry.id}`}
									aria-current={isActive ? "location" : undefined}
									className={cn(
										"block py-2 pl-3 text-sm leading-snug transition-colors lg:-ml-px lg:border-l-2 lg:py-1.5 lg:pl-4",
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
										isActive
											? "font-medium text-foreground lg:border-primary"
											: "text-muted-foreground hover:text-foreground lg:border-transparent lg:hover:border-border",
									)}
								>
									{entry.label}
								</a>
							</li>
						);
					})}
				</ul>
			</details>
		</nav>
	);
}

/**
 * "Back to top" for the long article templates: a plain button, kept out of the
 * DOM until one viewport of scroll, no live region, so a screen reader never
 * hears it on every scroll. NN/g: a back-to-top link saves multiple
 * scrolls on a long page and does not hurt the users who skip it
 * (https://www.nngroup.com/articles/in-page-links/). Instant under reduced motion.
 */
export function BackToTop() {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const onScroll = () => {
			setVisible(window.scrollY > window.innerHeight);
		};
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	const toTop = () => {
		const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
		window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
		document.getElementById(MAIN_CONTENT_ID)?.focus({ preventScroll: true });
	};

	// Not in the DOM until needed: a `hidden` attribute would lose to the
	// `inline-flex` utility (same specificity, later source), and an unmounted
	// button is also the quietest option for a screen reader.
	if (!visible) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={toTop}
			data-mp-id="doc-back-to-top"
			className="fixed bottom-4 right-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-foreground/80 shadow-sm transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background print:hidden"
		>
			<ArrowUp className="h-4 w-4" aria-hidden="true" />
			<span className="sr-only">Back to top</span>
		</button>
	);
}

/** Icon for a related link, by kind. */
function relatedIcon(link: DocRelatedLink): LucideIcon {
	if (link.icon) {
		return link.icon;
	}
	if (link.download) {
		return Download;
	}
	if (link.external) {
		return ArrowUpRight;
	}
	return FileText;
}

/** A single "Related" footer tile. */
function RelatedCard({ link }: { link: DocRelatedLink }) {
	const Icon = relatedIcon(link);
	// Flat warm card: solid `--card` fill, warm hairline, brand 20px radius, NO shadow.
	// The card lift on hover is owned by the global motion layer (`a[class*="rounded"]`).
	const cardClass =
		"group flex h-full items-start gap-3 rounded-2xl border border-border bg-card p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

	const inner = (
		<>
			<span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-background text-foreground transition-colors group-hover:border-primary/40 group-hover:text-primary">
				<Icon className="h-4 w-4" aria-hidden="true" />
			</span>
			<span className="min-w-0">
				<span className="block text-sm font-semibold leading-snug tracking-tight text-foreground group-hover:text-primary">
					{link.label}
				</span>
				{link.description && (
					<span className="mt-1 block text-sm leading-relaxed text-muted-foreground">{link.description}</span>
				)}
			</span>
		</>
	);

	if (link.external || link.download) {
		return (
			<a
				href={link.href}
				data-mp-id={`doc-related-${link.id}`}
				className={cardClass}
				{...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
				{...(link.download ? { download: true } : {})}
			>
				{inner}
			</a>
		);
	}

	return (
		<Link to={link.href} data-mp-id={`doc-related-${link.id}`} className={cardClass}>
			{inner}
		</Link>
	);
}

/**
 * The page shell for a bespoke Trust Center doc: the shared breadcrumb (Trust
 * Center, Documentation, this page; ABV-3631 made every write-up reachable both
 * ways), a hero band (eyebrow + title + lede +
 * category/last-updated meta), a two-column body (a max-prose content column + the
 * scroll-spy table of contents, a sticky rail at `lg+` and a collapsed block above
 * the prose below it), a "Related" footer, and back-to-top. Sets the document
 * title + meta.
 */
export function DocLayout({ eyebrow, title, lede, category, lastUpdated, toc, related, children }: DocLayoutProps) {
	usePageMeta({
		title: `${title} | Trust Center`,
		description: lede,
	});

	return (
		<div className="flex flex-col gap-8 pb-8">
			<Breadcrumb
				items={[
					{ label: "Trust Center", to: "/", mpId: "trust-doc-breadcrumb-home" },
					{ label: "Documentation", to: "/docs", mpId: "trust-doc-breadcrumb-docs" },
					{ label: title },
				]}
			/>

			{/* Hero band. */}
			<header>
				<div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
					<span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
					{eyebrow}
				</div>
				<h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-tight tracking-tight text-foreground sm:text-5xl">
					{title}
				</h1>
				<p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">{lede}</p>
				<div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-medium text-muted-foreground">
					<span className="inline-flex items-center gap-1.5">
						<FileText className="h-3.5 w-3.5" aria-hidden="true" />
						{category}
					</span>
					{lastUpdated && (
						<>
							<span className="hidden h-3 w-px bg-border sm:inline-block" aria-hidden="true" />
							<span className="inline-flex items-center gap-1.5">
								<CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
								{lastUpdated}
							</span>
						</>
					)}
				</div>
			</header>

			{/* Body: the TOC first in the DOM (one summary stop on a phone, the rail at
			    lg+ through grid order), then the prose column. */}
			<div className="grid grid-cols-1 gap-x-12 gap-y-6 lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-y-10">
				<aside className="lg:sticky lg:top-24 lg:order-last lg:self-start">
					<TableOfContents toc={toc} />
				</aside>
				<article className="flex min-w-0 flex-col gap-12">{children}</article>
			</div>

			{/* Related footer. */}
			{related && related.length > 0 && (
				<footer className="border-t border-border/60 pt-8 print:hidden">
					<div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
						<span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
						Related
					</div>
					<div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
						{related.map((link) => (
							<RelatedCard key={link.id} link={link} />
						))}
					</div>
				</footer>
			)}
			<BackToTop />
		</div>
	);
}
